'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { SUPPORT_EMAIL, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '@/constants/support';
import type { SupportContact, SupportContactContextValue } from './types';

// Fallback used everywhere the Studio fields are empty (or before the Header
// tastic has published). Single source for the constant defaults.
export const DEFAULT_SUPPORT_CONTACT: SupportContact = {
  phone: { display: SUPPORT_PHONE_DISPLAY, tel: SUPPORT_PHONE_TEL },
  email: SUPPORT_EMAIL,
};

const SupportContactContext = createContext<SupportContactContextValue>({
  contact: DEFAULT_SUPPORT_CONTACT,
  publish: () => {},
});

interface SupportContactProviderProps {
  // Optional server-seeded value (and a test seam). Runtime updates come via publish().
  initialContact?: SupportContact;
}

const sameContact = (a: SupportContact, b: SupportContact) =>
  a.phone.display === b.phone.display && a.phone.tel === b.phone.tel && a.email === b.email;

const SupportContactProvider = ({
  children,
  initialContact = DEFAULT_SUPPORT_CONTACT,
}: React.PropsWithChildren<SupportContactProviderProps>) => {
  const [contact, setContact] = useState<SupportContact>(initialContact);

  const publish = useCallback((next: SupportContact) => {
    setContact((prev) => (sameContact(prev, next) ? prev : next));
  }, []);

  const value = useMemo(() => ({ contact, publish }), [contact, publish]);

  return <SupportContactContext.Provider value={value}>{children}</SupportContactContext.Provider>;
};

export default SupportContactProvider;
export const useSupportContact = () => useContext(SupportContactContext);
